/**
 * Bid presets — starting points for the bid engine, one per job type.
 *
 * A preset only seeds the form; every number stays editable in the tool.
 * The `dorm_turnover_houston` numbers are sourced in
 * docs/bidding/houston-student-turnover-rates-2026.md. New job types
 * (office, residential move-out, …) get added here as they're researched
 * — we do NOT ship invented numbers for job types we haven't priced.
 */

import type { BidInputs } from './engine'

export interface BidPreset {
  key:         string
  label:       string
  description: string
  /** Where the default numbers come from (traceability). */
  sourceNote:  string
  defaults:    BidInputs
}

export const BID_PRESETS: readonly BidPreset[] = [
  {
    key:   'dorm_turnover_houston',
    label: 'Student dorm turnover — Houston',
    description:
      'Make-ready deep clean of a 2-bedroom student apartment at move-out: ' +
      'kitchen + oven/fridge, bath(s), bedrooms, ceiling fans, vents, floors. ' +
      'Heavy soil. Set add-on minutes to 60 for a 2-bath unit.',
    sourceNote:
      'docs/bidding/houston-student-turnover-rates-2026.md — sourced 2026 market pass. ' +
      '1BA ≈ $350/unit, 2BA ≈ $408/unit at these defaults.',
    defaults: {
      unitCount:               1,
      baseLaborMinutesPerUnit: 360,
      addOnMinutesPerUnit:     0,
      soilMultiplier:          1.5,
      crewSize:                2,
      burdenedHourlyRateCents: 2100,
      markupMultiplier:        1.85,
      flatMaterialsCents:      0,
    },
  },
  {
    key:   'custom',
    label: 'Custom job',
    description:
      'Blank starting point for any other job type (office/janitorial, ' +
      'residential move-out, one-off deep clean). Enter your own time, ' +
      'rate, and margin.',
    sourceNote: 'No preset numbers — you provide them.',
    defaults: {
      unitCount:               1,
      baseLaborMinutesPerUnit: 120,
      addOnMinutesPerUnit:     0,
      soilMultiplier:          1.0,
      crewSize:                1,
      burdenedHourlyRateCents: 2100,
      markupMultiplier:        2.0,
      flatMaterialsCents:      0,
    },
  },
] as const

export const DEFAULT_PRESET_KEY = 'dorm_turnover_houston'

/** Look up a preset by key, falling back to the first (dorm) preset. */
export function presetByKey(key: string): BidPreset {
  return BID_PRESETS.find((p) => p.key === key) ?? BID_PRESETS[0]
}
